import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import {
  Alert,
  Avatar,
  Badge,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { useMutation, useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { cs } from 'date-fns/locale';
import { first, flatMap, isEmpty } from 'lodash';
import React, { ReactNode, Suspense, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import {
  AlignLeft,
  BarChart02,
  Bell03,
  BookOpen02,
  CalendarPlus02,
  ChevronRight,
  File02,
  FilePlus02,
  HomeSmile,
  Menu01,
  MessageQuestionCircle,
  MessageTextSquare01,
  PasscodeLock,
  PencilLine,
  ReceiptCheck,
  Table,
  Truck01,
  UserLeft01,
  Users01,
  Wallet03
} from 'untitledui-js-base';
import { Notification, NotificationModel, useCommonApi } from '../api';
import { STALE_TIME } from '../api/constants';
import { LocalStorageKey } from '../constants';
import { PATHS } from '../paths';
import { AccidentCreationConfirmPage } from './AccidentCreationConfirmPage';
import { AccidentCreationPage } from './AccidentCreationPage';
import { AccidentDecisionPage } from './AccidentDecisionPage';
import { AccidentPage } from './AccidentPage';
import { AccidentsPage } from './AccidentsPage';
import { CashReceiptsPage } from './CashReceiptsPage';
import { ContactPage } from './ContactPage';
import { DashboardPage } from './DashboardPage';
import { DealerStatementPage } from './DealerStatementPage';
import { ExpenseDocumentsPage } from './ExpenseDocumentsPage';
import { IncomesPage } from './IncomesPage';
import { MessagesPage } from './MessagesPage';
import { PasswordChangePage } from './PasswordChangePage';
import { RentalPage } from './RentalPage';
import { RentalsPage } from './RentalsPage';
import { ServicesPage } from './ServicesPage';
import { StatisticsPage } from './StatisticsPage';
import { TippersPage } from './TippersPage';
import { UnpaidCommissionsPage } from './UnpaidCommissionsPage';
import { APP_HEADER_HEIGHT, AppHeader, FlexCenter, RouterLink } from './common';
import { AppContentContainer } from './common/AppContentContainer';
import { AppMenu, AppMenuItem, AppMenuSection } from './common/AppMenu';
import { useAuth } from './hooks';
import { Role, User } from './types';

interface RouteType {
  title: string;
  path: string;
  icon?: ReactNode;
  render: () => ReactNode;
  hidden?: boolean;
  badge?: React.ReactNode;
  role?: Role;
}

interface AppSection {
  title: string;
  items: RouteType[];
  role?: Role;
}

const toMenuItem = ({ title, path, icon, badge }: RouteType): AppMenuItem => [title, path, icon, badge];

const routes: Record<string, AppSection> = {
  dashboard: {
    title: 'Nástěnka',
    items: [
      {
        title: 'Nástěnka',
        path: PATHS.HOME,
        icon: <HomeSmile size="1.25rem" />,
        render: () => <DashboardPage />
      }
    ] as RouteType[]
  },
  general: {
    title: 'Nehody / Autopůjčovna',
    items: [
      {
        title: 'Řešení nehod',
        path: PATHS.ACCIDENTS(),
        icon: <File02 size="1.25rem" />,
        render: () => (
          <Routes>
            <Route
              path="/"
              element={
                <AccidentsPage accidentDetailPath={(accidentId) => PATHS.ACCIDENTS(Number.parseInt(accidentId))} />
              }
            />
            <Route path="/new" element={<AccidentDecisionPage />} />
            <Route path="/new/:accidentId" element={<AccidentCreationPage />} />
            <Route path="/:accidentId" element={<AccidentPage parentPath={PATHS.ACCIDENTS()} />} />
            <Route path="/confirm/:accidentId" element={<AccidentCreationConfirmPage />} />
          </Routes>
        )
      },
      {
        title: 'Autopůjčovna',
        path: PATHS.CAR_RENTAL(),
        icon: <File02 size="1.25rem" />,
        render: () => (
          <Routes>
            <Route
              path="/"
              element={<RentalsPage rentalDetailPath={(rentalId) => `${PATHS.CAR_RENTAL()}/${rentalId}`} />}
            />
            <Route path="/:rentalId" element={<RentalPage parentPath={PATHS.CAR_RENTAL()} />} />
          </Routes>
        )
      }
    ] as RouteType[]
  },
  cashRegister: {
    title: 'Pokladna',
    role: Role.Dealer,
    items: [
      {
        title: 'Pokladní doklady',
        path: PATHS.CASH_RECEIPTS,
        icon: <ReceiptCheck size="1.25rem" />,
        render: () => <CashReceiptsPage />,
        role: Role.Dealer
      }
    ]
  },
  finance: {
    title: 'Finance / Reporty',
    role: Role.Dealer,
    items: [
      {
        title: 'Statistiky',
        path: PATHS.STATISTICS,
        icon: <BarChart02 size="1.25rem" />,
        render: () => <StatisticsPage />,
        role: Role.Dealer
      },
      {
        title: 'Výkazy',
        path: PATHS.STATEMENTS,
        icon: <Table size="1.25rem" />,
        render: () => <DealerStatementPage />,
        role: Role.Dealer
      },
      {
        title: 'Výplaty',
        path: PATHS.INCOMES,
        icon: <Wallet03 size="1.25rem" />,
        render: () => <IncomesPage />,
        role: Role.Dealer
      },
      {
        title: 'POJ k vyplacení',
        path: PATHS.UNPAID_COMMISSIONS,
        icon: <Wallet03 size="1.25rem" />,
        render: () => <UnpaidCommissionsPage />,
        role: Role.Dealer
      },
      {
        title: 'Výdajové doklady',
        path: PATHS.EXPENSE_DOCUMENTS,
        icon: <ReceiptCheck size="1.25rem" />,
        render: () => <ExpenseDocumentsPage />,
        role: Role.Dealer
      }
    ] as RouteType[]
  },
  registry: {
    title: 'Číselníky',
    role: Role.Dealer,
    items: [
      {
        title: 'Tipaři',
        path: PATHS.TIPPERS(),
        icon: <Users01 size="1.25rem" />,
        render: () => (
          <Routes>
            <Route path="/" element={<TippersPage />} />
          </Routes>
        ),
        role: Role.Dealer
      },
      {
        title: 'Servisy',
        path: PATHS.SERVICES(),
        icon: <Truck01 size="1.25rem" />,
        render: () => (
          <Routes>
            <Route path="/" element={<ServicesPage />} />
          </Routes>
        ),
        role: Role.Dealer
      }
    ] as RouteType[]
  },
  info: {
    title: 'Ostatní',
    items: [
      {
        title: 'Zprávy z RPS',
        path: PATHS.MESSAGES,
        icon: <BookOpen02 size="1.25rem" />,
        render: () => <MessagesPage />
      },
      {
        title: 'Kontaktujte nás',
        path: PATHS.CONTACTS,
        icon: <MessageTextSquare01 size="1.25rem" />,
        render: () => <ContactPage />
      }
    ] as RouteType[]
  },
  user: {
    title: 'User',
    items: [
      {
        title: 'Změna hesla',
        path: PATHS.CHANGE_PASSWORD,
        render: () => <PasswordChangePage />
      }
    ] as RouteType[]
  }
} satisfies Record<string, AppSection>;

interface BTLNetHeaderTitleProps {
  variant?: 'default' | 'dark';
}

export const BTLNetHeaderTitle = ({ variant = 'default' }: BTLNetHeaderTitleProps) => {
  const { src, color } = useMemo(
    () => ({
      src: variant === 'dark' ? '/rps_logo.svg' : '/logo_white.svg',
      color: variant === 'dark' ? 'text.primary' : 'common.white'
    }),
    [variant]
  );

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      {src && <Box component="img" src={src} alt="RPS logo" height="33px" />}
      <Typography
        sx={{
          textTransform: 'uppercase',
          fontWeight: 'bold',
          color,
          display: {
            xs: 'none',
            sm: 'inline-block'
          }
        }}
        typography="body2"
      >
        Systém řešení nehod
      </Typography>
    </Stack>
  );
};

interface UserDropdownMenuProps {
  avatarSrc?: string;
  userName: string;
  onNavigate: (path: string) => void;
}

const UserDropdownMenu = ({ userName, avatarSrc, onNavigate }: UserDropdownMenuProps) => {
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    if (!anchorElUser) setAnchorElUser(event.currentTarget);
    else setAnchorElUser(null);
  };
  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const userInitials = (userName: string) => {
    const [firstName, lastName] = userName.split(' ');
    return `${firstName[0]}${lastName[0]}`;
  };

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      onClick={handleOpenUserMenu}
      sx={{
        cursor: 'pointer',
        paddingY: 0.5,
        paddingLeft: 0.5,
        paddingRight: { xs: 0.5, md: 1.5 },
        borderRadius: 2,
        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.12)' }
      }}
    >
      <Avatar
        sx={{
          width: 36,
          height: 36,
          fontSize: 'medium',
          fontWeight: 700,
          backgroundColor: '#fff',
          color: 'background.header'
        }}
        src={avatarSrc}
      >
        {userInitials(userName)}
      </Avatar>
      <Box typography="body2" paddingLeft={1} fontWeight={500} display={{ xs: 'none', md: 'inline-block' }}>
        {userName}
      </Box>
      <Menu
        sx={{ mt: 3 }}
        anchorEl={anchorElUser}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        open={Boolean(anchorElUser)}
        onClose={handleCloseUserMenu}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0
              }
            }
          }
        }}
      >
        <MenuItem onClick={() => onNavigate(PATHS.CHANGE_PASSWORD)}>
          <ListItemIcon
            sx={{
              justifyContent: 'center',
              alignItems: 'center',
              color: 'inherit'
            }}
          >
            <PasscodeLock size="20px" />
          </ListItemIcon>
          <ListItemText
            primary="Změna hesla"
            sx={{
              ml: 0,
              overflow: 'hidden',
              textWrap: 'nowrap',
              color: 'inherit'
            }}
            primaryTypographyProps={{ fontWeight: 500 }}
          />
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            handleCloseUserMenu();
            //TODO: this is just a temporary way to log out right now
            localStorage.removeItem(LocalStorageKey.SessionToken);
            window.location.reload();
          }}
        >
          <ListItemIcon
            sx={{
              justifyContent: 'center',
              alignItems: 'center',
              color: 'inherit'
            }}
          >
            <UserLeft01 size="20px" />
          </ListItemIcon>
          <ListItemText
            primary="Odhlásit se"
            sx={{
              ml: 0,
              overflow: 'hidden',
              textWrap: 'nowrap',
              color: 'inherit'
            }}
            primaryTypographyProps={{ fontWeight: 500 }}
          />
        </MenuItem>
      </Menu>
    </Stack>
  );
};

interface NotificationItemProps {
  notification: Notification;
  onClick?: (id: number, url?: string) => void;
}

const NotificationItem = ({
  notification: { id, createdDate, viewed, objectModel, extraData },
  onClick
}: NotificationItemProps) => {
  const getIconComponent = (type: NotificationModel) => {
    switch (type) {
      case NotificationModel.Event:
        return {
          icon: <CalendarPlus02 size="1.25rem" />,
          color: 'success.main'
        };
      case NotificationModel.Article:
        return { icon: <AlignLeft size="1.25rem" />, color: 'primary.main' };
      case NotificationModel.Document:
        return { icon: <FilePlus02 size="1.25rem" />, color: 'primary.main' };
      case NotificationModel.Request:
        return {
          icon: <MessageQuestionCircle size="1.25rem" />,
          color: 'warning.main'
        };
      case NotificationModel.Questionnaire:
        return { icon: <PencilLine size="1.25rem" />, color: 'error.main' };
      default:
        return { icon: <Bell03 size="20px" />, color: 'warning.main' }; // Default icon and color
    }
  };

  return (
    <MenuItem
      sx={{
        paddingY: 2,
        backgroundColor: !viewed ? 'background.default' : '#fff'
      }}
      onClick={() => onClick?.(id, extraData?.url)}
    >
      <Stack direction="row" justifyContent="space-between" width="100%" alignItems="center">
        <Stack spacing={2} direction="row" alignItems="center">
          <Badge
            variant="dot"
            color="error"
            overlap="circular"
            badgeContent=" "
            invisible={!extraData?.isMandatory || viewed}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'left'
            }}
            sx={{ '& .MuiBadge-badge': { borderRadius: '30px' } }}
          >
            <Avatar
              variant="circular"
              sx={{
                backgroundColor: !viewed ? getIconComponent(objectModel).color : 'grey.light',
                width: 30,
                height: 30
              }}
            >
              {getIconComponent(objectModel).icon}
            </Avatar>
          </Badge>
          <ListItemText
            sx={{ maxWidth: '280px' }}
            primary={extraData?.title}
            secondary={`${objectModel} | ${format(createdDate, 'dd.MM.yyyy HH:mm')}`}
            primaryTypographyProps={{
              fontWeight: 700,
              style: { textWrap: 'pretty' }
            }}
            secondaryTypographyProps={{ fontSize: '0.75rem' }}
          />
        </Stack>
        <ChevronRight />
      </Stack>
    </MenuItem>
  );
};

interface NotificationPanelProps {
  notifications?: Notification[];
  onClickNotification?: (id: number, url?: string) => void;
}

const NotificationPanel = ({ notifications, onClickNotification }: NotificationPanelProps) => {
  const [anchorElNotification, setAnchorElNotification] = React.useState<null | HTMLElement>(null);

  const handleOpenNotificationPanel = (event: React.MouseEvent<HTMLElement>) => {
    if (!anchorElNotification) setAnchorElNotification(event.currentTarget);
    else setAnchorElNotification(null);
  };
  const handleCloseNotificationPanel = () => {
    setAnchorElNotification(null);
  };

  return (
    <>
      <IconButton
        onClick={handleOpenNotificationPanel}
        sx={{ borderRadius: 2, '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.12)' } }}
      >
        <Badge
          badgeContent={notifications?.filter((notification) => !notification.viewed).length}
          color="warning"
          max={99}
          overlap="circular"
        >
          <NotificationsNoneOutlinedIcon sx={{ color: 'common.white' }} />
        </Badge>
      </IconButton>
      <Menu
        sx={{ mt: 3 }}
        id="notifications-bar"
        anchorEl={anchorElNotification}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        open={Boolean(anchorElNotification)}
        onClose={handleCloseNotificationPanel}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: { xs: 67, md: 14 },
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0
              }
            }
          }
        }}
      >
        <Box paddingY={1} paddingX={2} minWidth="400px">
          <Typography variant="h6">Notifikace</Typography>
        </Box>
        <Divider />
        <Box sx={{ maxHeight: '350px', overflowY: 'auto' }}>
          {!isEmpty(notifications) ? (
            notifications?.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onClick={(id, url) => {
                  onClickNotification?.(id, url);
                  handleCloseNotificationPanel();
                }}
              />
            ))
          ) : (
            <Box textAlign="center" py={3}>
              <Typography variant="body2">Žádné notifikace...</Typography>
            </Box>
          )}
        </Box>
      </Menu>
    </>
  );
};

interface BTLNetAppHeaderProps {
  notifications?: Notification[];
  onClickNotification?: (id: number, url?: string) => void;
  user: User;
  onMenuClick: () => void;
  onNavigate: (path: string) => void;
}

const RPSAppHeader = ({
  notifications,
  onClickNotification,
  user: { avatarSrc, fullName: userName },
  onMenuClick,
  onNavigate
}: BTLNetAppHeaderProps) => {
  return (
    <AppHeader
      extra={
        <>
          <NotificationPanel notifications={notifications} onClickNotification={onClickNotification} />
          <UserDropdownMenu userName={userName} avatarSrc={avatarSrc} onNavigate={onNavigate} />
        </>
      }
    >
      <Stack direction="row" alignItems="center" gap={2}>
        <IconButton color="inherit" aria-label="open menu" onClick={onMenuClick} sx={{ display: { md: 'none' } }}>
          <Menu01 />
        </IconButton>
        <BTLNetHeaderTitle />
      </Stack>
    </AppHeader>
  );
};

interface RPSAppContentProps {
  sections: AppMenuSection[];
  defaultRoutePath: string;
}

const RPSAppContent = ({ sections, defaultRoutePath }: RPSAppContentProps) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  const { user: authUser } = useAuth();
  /* const { data: notifications } = useQuery({
                                                           ...useCommonApi().getNotifications(),
                                                           refetchInterval: STALE_TIME.HALF_MINUTE,
                                                         });*/
  const { data: accidentsWaiting } = useQuery({
    ...useCommonApi().getAccidentsWaiting(),
    refetchInterval: STALE_TIME.SHORT
  });
  const { mutate: setNotificationStatus } = useMutation(useCommonApi().setNotificationStatus());

  if (authUser === null) return null;

  return (
    <>
      <RPSAppHeader
        //notifications={notifications}
        onClickNotification={(id, url) => {
          if (url) navigate(url);
          setNotificationStatus({ id });
        }}
        user={authUser}
        onMenuClick={() => setMenuOpen(!menuOpen)}
        onNavigate={(path) => navigate(path)}
      />
      <AppContentContainer display="flex" sx={{ paddingTop: `${APP_HEADER_HEIGHT}px` }}>
        <AppMenu sections={sections} menuOpen={menuOpen} onClose={() => setMenuOpen(false)} />
        <Box
          sx={{
            width: 1,
            position: 'relative',
            maxWidth: '100%',
            overflowX: 'hidden',
            height: 1
          }}
        >
          <Suspense
            fallback={
              <FlexCenter>
                <CircularProgress />
              </FlexCenter>
            }
          >
            {accidentsWaiting && !isEmpty(accidentsWaiting) && (
              <Alert
                severity="error"
                sx={{
                  marginX: { xs: 2, md: 4 },
                  marginTop: 2,
                  border: '1px solid rgba(198, 35, 39, 0.2)',
                  alignItems: 'center'
                }}
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" width="100%">
                  {accidentsWaiting.length > 1 ? 'Některé nehody vyžadují doplnění.' : 'Nehoda vyžaduje doplnění.'}

                  <RouterLink to={PATHS.ACCIDENTS(accidentsWaiting.length === 1 ? accidentsWaiting[0] : undefined)}>
                    <Link
                      component="span"
                      underline="always"
                      color="inherit"
                      sx={{
                        display: 'inline-block',
                        marginLeft: '5px'
                      }}
                    >
                      {accidentsWaiting.length > 1 ? 'Přejít na seznam nehod' : 'Zobrazit nehodu'}
                    </Link>
                  </RouterLink>
                </Stack>
              </Alert>
            )}
            <Routes>
              {flatMap(routes, (route) =>
                route.items.map(({ path, render }) => <Route key={path} path={`${path}/*`} element={render()} />)
              )}
              <Route path="*" element={<Navigate to={defaultRoutePath} replace />} />
            </Routes>
          </Suspense>
        </Box>
      </AppContentContainer>
    </>
  );
};

export const RPSApp = () => {
  const defaultRoute = first(routes.general.items);
  const { user } = useAuth();
  if (!defaultRoute) return <>Insufficient permissions.</>;

  const sections: AppMenuSection[] = Object.entries(routes)
    .filter(([key]) => key !== 'user')
    .filter(([, section]) => !section.role || section.role === user?.role)
    .map(([, { title, items }]) => ({
      title,
      items: items.filter(({ role }) => role === undefined || role === user?.role).map(toMenuItem)
    }))
    .filter(({ items }) => items.length > 0);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={cs}>
      <RPSAppContent defaultRoutePath={defaultRoute.path} sections={sections} />
    </LocalizationProvider>
  );
};
